import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { detectPlatform, isPhone } from '../../lib/install/device'
import {
  countInstallVisit,
  finishInstallCard,
  useDeviceReading,
  useInstallMemory,
  useInstallPromptState,
} from '../../lib/install/hooks'
import { shouldShowInstallCard } from '../../lib/install/memory'
import { promptInstall } from '../../lib/install/prompt-store'

export interface InstallCardViewProps {
  /** `install` opens the browser's own dialog; `how-to` goes to the page with the steps. */
  action: 'install' | 'how-to'
  onInstall: () => void
  onDismiss: () => void
}

const ACTION_CLASS = 'inline-flex min-h-11 min-w-11 items-center justify-center rounded-xl px-5 text-sm font-medium transition-opacity hover:opacity-90'
const ACTION_STYLE = { background: '#3D1F62', color: 'white', border: 'none', cursor: 'pointer', textDecoration: 'none' } as const

/** The small card on the home screen. Presentational: `InstallCard` decides whether it shows at all. */
export function InstallCardView({ action, onInstall, onDismiss }: InstallCardViewProps) {
  const { t } = useTranslation()
  return (
    <aside
      className="mb-5 rounded-2xl px-4 py-4 message-appear"
      style={{ background: 'rgba(155,114,192,0.08)', border: '1.5px solid rgba(155,114,192,0.22)' }}
    >
      <p className="text-sm font-medium mb-0.5" style={{ color: '#1C1128' }}>{t('installCard.title')}</p>
      <p className="text-xs leading-relaxed" style={{ color: '#6B5F7A' }}>{t('installCard.body')}</p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {action === 'install' ? (
          <button type="button" onClick={onInstall} className={ACTION_CLASS} style={ACTION_STYLE}>
            {t('installCard.install')}
          </button>
        ) : (
          <Link to="/install" className={ACTION_CLASS} style={ACTION_STYLE}>
            {t('installCard.howTo')}
          </Link>
        )}
        <button
          type="button"
          onClick={onDismiss}
          className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-xl px-4 text-sm hover:underline"
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9B8FAA', textUnderlineOffset: 4 }}
        >
          {t('installCard.dismiss')}
        </button>
      </div>
    </aside>
  )
}

/**
 * The home-screen nudge on Home. Shown on a phone browser that has not
 * installed the app, from the second visit on, until it is dismissed or the
 * app is installed. Never inside the installed app, and never on a computer:
 * a computer gets bookmark steps on /install instead.
 */
export function InstallCard() {
  const device = useDeviceReading()
  const prompt = useInstallPromptState()
  const memory = useInstallMemory()

  const isInstalled = device.isStandalone || prompt.isInstalled

  useEffect(() => {
    countInstallVisit()
  }, [])

  // Installed, in the app or from the browser's dialog: never ask on this device again.
  useEffect(() => {
    if (isInstalled) finishInstallCard()
  }, [isInstalled])

  if (!shouldShowInstallCard({ isPhone: isPhone(device), isStandalone: isInstalled, visits: memory.visits, isDone: memory.isDone })) {
    return null
  }

  const canOpenDialog = detectPlatform(device) !== 'ios' && prompt.event !== null

  return (
    <InstallCardView
      action={canOpenDialog ? 'install' : 'how-to'}
      onInstall={() => void promptInstall()}
      onDismiss={finishInstallCard}
    />
  )
}
